import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Settings2, Trash2 } from 'lucide-react'
import { useOrganizationUsers, type OrganizationUser } from '../hooks/use-organization-users'
import { usePendingInvitations, useResendInvitation, useCancelInvitation, type Invitation } from '../hooks/use-invitations'
import { useUpdateUserRole } from '../hooks/use-update-user-role'
import { useRemoveUser } from '../hooks/use-remove-user'
import { useCurrentUser } from '@/features/auth/hooks/use-current-user'
import { InviteUserModal } from './invite-user-modal'
import { EditUserTeamsModal } from './edit-user-teams-modal'
import { ConfirmModal } from '@/shared/components/confirm-modal'
import { cn } from '@/shared/lib/utils'
import type { UserRole } from '@/shared/types'

const ROLES: UserRole[] = ['admin', 'user', 'viewer']

type SortField = 'name' | 'role' | 'created_at'
type SortOrder = 'asc' | 'desc'

function RoleBadge({ role }: { role: UserRole }) {
  const { t } = useTranslation()

  const colorClasses = {
    admin: 'bg-accent/10 text-accent border-accent/20',
    user: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    viewer: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  }

  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border',
      colorClasses[role]
    )}>
      {t(`settings.team.roles.${role}`)}
    </span>
  )
}

function StatusBadge({ status }: { status: 'active' | 'pending' | 'expired' }) {
  const { t } = useTranslation()

  const colorClasses = {
    active: 'bg-green-500/10 text-green-600 border-green-500/20',
    pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    expired: 'bg-red-500/10 text-red-600 border-red-500/20',
  }

  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border',
      colorClasses[status]
    )}>
      {t(`settings.team.status.${status}`)}
    </span>
  )
}

function UserAvatar({ user }: { user: OrganizationUser }) {
  const initials = user.full_name
    ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user.email[0].toUpperCase()

  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={user.full_name || user.email}
        className="h-10 w-10 rounded-full object-cover"
      />
    )
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-sm font-medium text-accent">
      {initials}
    </div>
  )
}

function InvitationAvatar({ email }: { email: string }) {
  const initials = email[0].toUpperCase()

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-500/10 text-sm font-medium text-yellow-600">
      {initials}
    </div>
  )
}

function RoleSelect({
  user,
  isCurrentUser,
  onRoleChange
}: {
  user: OrganizationUser
  isCurrentUser: boolean
  onRoleChange: (userId: string, newRole: UserRole) => void
}) {
  const { t } = useTranslation()

  if (isCurrentUser) {
    // Current user cannot change their own role - show badge only
    return <RoleBadge role={user.role} />
  }

  return (
    <select
      value={user.role}
      onChange={(e) => onRoleChange(user.id, e.target.value as UserRole)}
      className="rounded-lg border border-border bg-background px-2 py-1 text-xs font-medium focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent cursor-pointer"
    >
      {ROLES.map(role => (
        <option key={role} value={role}>
          {t(`settings.team.roles.${role}`)}
        </option>
      ))}
    </select>
  )
}

function UserRow({
  user,
  isCurrentUser,
  onRoleChange,
  onEditTeams,
  onRemove
}: {
  user: OrganizationUser
  isCurrentUser: boolean
  onRoleChange: (userId: string, newRole: UserRole) => void
  onEditTeams: (user: OrganizationUser) => void
  onRemove: (user: OrganizationUser) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-background p-4">
      <div className="flex items-center gap-4">
        <UserAvatar user={user} />
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">
              {user.full_name || t('settings.team.noName')}
            </span>
            {isCurrentUser && (
              <span className="text-xs text-foreground-muted">
                ({t('settings.team.you')})
              </span>
            )}
          </div>
          <span className="text-sm text-foreground-muted">{user.email}</span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Teams with edit button */}
        <div className="hidden sm:flex items-center gap-2">
          {user.teams.length > 0 ? (
            user.teams.length <= 2 ? (
              user.teams.map(team => (
                <span
                  key={team.id}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-background-secondary text-foreground-muted border border-border"
                >
                  {team.name}
                </span>
              ))
            ) : (
              <span className="text-xs text-foreground-muted">
                {t('settings.team.teamsCount', { count: user.teams.length })}
              </span>
            )
          ) : (
            <span className="text-xs text-foreground-muted">
              {t('settings.team.noTeams')}
            </span>
          )}
          <button
            onClick={() => onEditTeams(user)}
            className="p-1 rounded text-foreground-muted hover:text-accent hover:bg-accent/10 transition-colors"
            title={t('settings.team.editTeams.button')}
          >
            <Settings2 className="h-4 w-4" />
          </button>
        </div>

        {/* Status */}
        <StatusBadge status="active" />

        {/* Role Select */}
        <RoleSelect
          user={user}
          isCurrentUser={isCurrentUser}
          onRoleChange={onRoleChange}
        />

        {/* Remove button - only visible if not current user */}
        {!isCurrentUser && (
          <button
            onClick={() => onRemove(user)}
            className="p-1.5 rounded text-foreground-muted hover:text-red-600 hover:bg-red-50 transition-colors"
            title={t('settings.team.removeUser')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

function InvitationRow({ invitation, onResend, onCancel, isResending }: {
  invitation: Invitation
  onResend: () => void
  onCancel: () => void
  isResending: boolean
}) {
  const { t } = useTranslation()

  const isExpired = new Date(invitation.expires_at) < new Date()

  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-background p-4">
      <div className="flex items-center gap-4">
        <InvitationAvatar email={invitation.email} />
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">
              {invitation.email}
            </span>
          </div>
          <span className="text-xs text-foreground-muted">
            {t('settings.team.invitedBy', {
              name: invitation.inviter?.full_name || invitation.inviter?.email || t('settings.team.unknown')
            })}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Teams */}
        <div className="hidden sm:flex items-center gap-2">
          {invitation.teams.length > 0 ? (
            invitation.teams.length <= 2 ? (
              invitation.teams.map(team => (
                <span
                  key={team.id}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-background-secondary text-foreground-muted border border-border"
                >
                  {team.name}
                </span>
              ))
            ) : (
              <span className="text-xs text-foreground-muted">
                {t('settings.team.teamsCount', { count: invitation.teams.length })}
              </span>
            )
          ) : (
            <span className="text-xs text-foreground-muted">
              {t('settings.team.noTeams')}
            </span>
          )}
        </div>

        {/* Status */}
        <StatusBadge status={isExpired ? 'expired' : 'pending'} />

        {/* Role Badge */}
        <RoleBadge role={invitation.role} />

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={onResend}
            disabled={isResending}
            className="text-xs text-accent hover:text-accent/80 disabled:opacity-50"
            title={t('settings.team.resendInvitation')}
          >
            {isResending ? t('settings.team.resending') : t('settings.team.resend')}
          </button>
          <button
            onClick={onCancel}
            className="text-xs text-red-600 hover:text-red-500"
            title={t('settings.team.cancelInvitation')}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}

function SearchInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation()

  return (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        type="text"
        placeholder={t('settings.team.searchPlaceholder')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-4 text-sm placeholder:text-foreground-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </div>
  )
}

function SortSelect({ value, onChange }: { value: SortField; onChange: (value: SortField) => void }) {
  const { t } = useTranslation()

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as SortField)}
      className="rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
    >
      <option value="name">{t('settings.team.sortByName')}</option>
      <option value="role">{t('settings.team.sortByRole')}</option>
      <option value="created_at">{t('settings.team.sortByDate')}</option>
    </select>
  )
}

export function TeamTab() {
  const { t } = useTranslation()
  const { user: currentUser } = useCurrentUser()
  const { data: users, isLoading: usersLoading, error: usersError } = useOrganizationUsers()
  const { data: invitations, isLoading: invitationsLoading } = usePendingInvitations()
  const resendInvitation = useResendInvitation()
  const cancelInvitation = useCancelInvitation()
  const updateUserRole = useUpdateUserRole()
  const removeUser = useRemoveUser()

  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortOrder] = useState<SortOrder>('asc')
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [resendingId, setResendingId] = useState<string | null>(null)

  // Role change confirmation modal state
  const [roleChangeModal, setRoleChangeModal] = useState<{
    isOpen: boolean
    userId: string
    userName: string
    currentRole: UserRole
    newRole: UserRole
  } | null>(null)

  // Edit teams modal state
  const [editTeamsModal, setEditTeamsModal] = useState<{
    isOpen: boolean
    userId: string
    userName: string
    currentTeamIds: string[]
  } | null>(null)

  // Remove user confirmation modal state
  const [removeUserModal, setRemoveUserModal] = useState<{
    isOpen: boolean
    userId: string
    userName: string
    userRole: UserRole
  } | null>(null)

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    if (!users) return []

    let filtered = users

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(user =>
        (user.full_name?.toLowerCase().includes(query)) ||
        user.email.toLowerCase().includes(query)
      )
    }

    // Sort
    const roleOrder = { admin: 0, user: 1, viewer: 2 }
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'name':
          comparison = (a.full_name || a.email).localeCompare(b.full_name || b.email)
          break
        case 'role':
          comparison = roleOrder[a.role] - roleOrder[b.role]
          break
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })

    return filtered
  }, [users, searchQuery, sortField, sortOrder])

  // Filter invitations by search
  const filteredInvitations = useMemo(() => {
    if (!invitations) return []

    if (!searchQuery.trim()) return invitations

    const query = searchQuery.toLowerCase()
    return invitations.filter(inv => inv.email.toLowerCase().includes(query))
  }, [invitations, searchQuery])

  const handleResend = async (invitationId: string) => {
    setResendingId(invitationId)
    try {
      await resendInvitation.mutateAsync(invitationId)
    } finally {
      setResendingId(null)
    }
  }

  const handleCancel = async (invitationId: string) => {
    await cancelInvitation.mutateAsync(invitationId)
  }

  const handleRoleChangeRequest = (userId: string, newRole: UserRole) => {
    const targetUser = users?.find(u => u.id === userId)
    if (!targetUser || targetUser.role === newRole) return

    setRoleChangeModal({
      isOpen: true,
      userId,
      userName: targetUser.full_name || targetUser.email,
      currentRole: targetUser.role,
      newRole,
    })
  }

  const handleRoleChangeConfirm = async () => {
    if (!roleChangeModal) return

    try {
      await updateUserRole.mutateAsync({
        userId: roleChangeModal.userId,
        newRole: roleChangeModal.newRole,
      })
      toast.success(t('settings.team.roleUpdated'))
      setRoleChangeModal(null)
    } catch (error) {
      if (error instanceof Error && error.message === 'CANNOT_CHANGE_OWN_ROLE') {
        toast.error(t('settings.team.cannotChangeOwnRole'))
      } else {
        toast.error(t('common.error'))
      }
    }
  }

  const handleEditTeams = (user: OrganizationUser) => {
    setEditTeamsModal({
      isOpen: true,
      userId: user.id,
      userName: user.full_name || user.email,
      currentTeamIds: user.teams.map(t => t.id),
    })
  }

  const handleRemoveUserRequest = (user: OrganizationUser) => {
    setRemoveUserModal({
      isOpen: true,
      userId: user.id,
      userName: user.full_name || user.email,
      userRole: user.role,
    })
  }

  const handleRemoveUserConfirm = async () => {
    if (!removeUserModal) return

    try {
      await removeUser.mutateAsync(removeUserModal.userId)
      toast.success(t('settings.team.userRemoved'))
      setRemoveUserModal(null)
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case 'CANNOT_REMOVE_SELF':
            toast.error(t('settings.team.cannotRemoveSelf'))
            break
          case 'CANNOT_REMOVE_LAST_ADMIN':
            toast.error(t('settings.team.cannotRemoveLastAdmin'))
            break
          case 'PERMISSION_DENIED':
            toast.error(t('settings.team.permissionDenied'))
            break
          case 'USER_NOT_FOUND':
            toast.error(t('settings.team.userNotFound'))
            break
          default:
            toast.error(t('common.error'))
        }
      } else {
        toast.error(t('common.error'))
      }
    }
  }

  const isLoading = usersLoading || invitationsLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    )
  }

  if (usersError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        {t('common.error')}
      </div>
    )
  }

  const totalCount = filteredUsers.length + filteredInvitations.length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          {t('settings.team.title')}
        </h2>
        <p className="mt-1 text-sm text-foreground-muted">
          {t('settings.team.description')}
        </p>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-3">
          <div className="flex-1 sm:max-w-xs">
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
          </div>
          <SortSelect value={sortField} onChange={setSortField} />
        </div>
        <button
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors"
          onClick={() => setIsInviteModalOpen(true)}
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('settings.team.inviteUser')}
        </button>
      </div>

      {/* Users Count */}
      <div className="text-sm text-foreground-muted">
        {t('settings.team.usersCount', { count: totalCount })}
        {filteredInvitations.length > 0 && (
          <span className="ml-1">
            ({t('settings.team.pendingCount', { count: filteredInvitations.length })})
          </span>
        )}
      </div>

      {/* Users List */}
      {totalCount === 0 ? (
        <div className="rounded-lg border border-border bg-background-secondary p-8 text-center">
          <p className="text-foreground-muted">
            {searchQuery ? t('settings.team.noResults') : t('settings.team.noUsers')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Active users first */}
          {filteredUsers.map(user => (
            <UserRow
              key={user.id}
              user={user}
              isCurrentUser={user.id === currentUser?.id}
              onRoleChange={handleRoleChangeRequest}
              onEditTeams={handleEditTeams}
              onRemove={handleRemoveUserRequest}
            />
          ))}

          {/* Then pending invitations */}
          {filteredInvitations.map(invitation => (
            <InvitationRow
              key={invitation.id}
              invitation={invitation}
              onResend={() => handleResend(invitation.id)}
              onCancel={() => handleCancel(invitation.id)}
              isResending={resendingId === invitation.id}
            />
          ))}
        </div>
      )}

      {/* Invite Modal */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      {/* Role Change Confirmation Modal */}
      {roleChangeModal && (
        <ConfirmModal
          isOpen={roleChangeModal.isOpen}
          onClose={() => setRoleChangeModal(null)}
          onConfirm={handleRoleChangeConfirm}
          isLoading={updateUserRole.isPending}
          title={t('settings.team.changeRoleTitle')}
          description={t('settings.team.changeRoleDescription', {
            name: roleChangeModal.userName,
            fromRole: t(`settings.team.roles.${roleChangeModal.currentRole}`),
            toRole: t(`settings.team.roles.${roleChangeModal.newRole}`),
          })}
          confirmLabel={t('settings.team.changeRole')}
          variant={roleChangeModal.newRole === 'viewer' || roleChangeModal.currentRole === 'admin' ? 'destructive' : 'default'}
        />
      )}

      {/* Edit Teams Modal */}
      {editTeamsModal && (
        <EditUserTeamsModal
          isOpen={editTeamsModal.isOpen}
          onClose={() => setEditTeamsModal(null)}
          userId={editTeamsModal.userId}
          userName={editTeamsModal.userName}
          currentTeamIds={editTeamsModal.currentTeamIds}
        />
      )}

      {/* Remove User Confirmation Modal */}
      {removeUserModal && (
        <ConfirmModal
          isOpen={removeUserModal.isOpen}
          onClose={() => setRemoveUserModal(null)}
          onConfirm={handleRemoveUserConfirm}
          isLoading={removeUser.isPending}
          title={t('settings.team.removeUserTitle')}
          description={t('settings.team.removeUserDescription', {
            name: removeUserModal.userName,
          })}
          confirmLabel={t('settings.team.removeUserConfirm')}
          variant="destructive"
        />
      )}
    </div>
  )
}
